import { APIService } from '../../../lib/api/APIService';

/**
 * The read API (workspace browse) and the live runtime (sittings), both
 * under /api/interviews. One client for both, since a browse screen and a
 * live sitting screen are steps in the same flow, not separate features.
 *
 * transcribeAudioBlob's base64 encoding is lifted from
 * features/website-interview/api/websiteInterviewAPI.js verbatim — it
 * already matches POST /transcribe's contract (audio_data as base64 JSON,
 * not multipart), no reason to re-derive it.
 */
// The default request timeout is 30s. Three calls here wait on a model and
// can outlast it: the sitting report is one narrative call across the whole
// sitting (13-17s measured on production, and the backend allows it 60s plus
// one retry), the sketch check is a vision call, and transcription grows with
// the length of the answer. Aborting at 30s would show "could not load" for
// a report the server was still writing.
const MODEL_CALL_TIMEOUT_MS = 60000;
const REPORT_TIMEOUT_MS = 90000;

class InterviewRuntimeAPI extends APIService {
  // ---- read API: workspace browse -----------------------------------
  async getWorkspaces() {
    return this.get('/api/interviews/workspaces');
  }

  async getLeafPapers(leafKey) {
    return this.get(`/api/interviews/leaves/${encodeURIComponent(leafKey)}/papers`);
  }

  // Everything a course offers, laid out: its live interviews and its mock
  // interview papers' interviews, with this student's completion of each.
  async getLeafOverview(leafKey) {
    return this.get(`/api/interviews/leaves/${encodeURIComponent(leafKey)}/overview`);
  }

  async getPaper(paperId) {
    return this.get(`/api/interviews/papers/${encodeURIComponent(paperId)}`);
  }

  // ---- runtime: one question = one run --------------------------------
  // mode 'written' is a mock interview paper answered in writing: the same
  // runtime, less what needs someone in the room (written.py).
  async startRun(questionId, { mode } = {}) {
    return this.post('/api/interviews/runs', { question_id: questionId, ...(mode ? { mode } : {}) });
  }

  async submitTurn(runId, reply, { mode } = {}) {
    return this.post(`/api/interviews/runs/${encodeURIComponent(runId)}/turns`, { reply, ...(mode ? { mode } : {}) });
  }

  // A written paper's hint button, and finishing a question left part-way.
  async takeHint(runId) {
    return this.post(`/api/interviews/runs/${encodeURIComponent(runId)}/hint`, {});
  }

  async endRun(runId) {
    return this.post(`/api/interviews/runs/${encodeURIComponent(runId)}/end`, {});
  }

  async getRun(runId) {
    return this.get(`/api/interviews/runs/${encodeURIComponent(runId)}`);
  }

  async checkComputation(runId, answer) {
    return this.post(
      `/api/interviews/runs/${encodeURIComponent(runId)}/check-computation`,
      { answer },
    );
  }

  async checkSketch(runId, imageDataUrl) {
    return this.post(
      `/api/interviews/runs/${encodeURIComponent(runId)}/check-sketch`,
      { image: imageDataUrl },
      { timeout: MODEL_CALL_TIMEOUT_MS },
    );
  }

  // ---- voice -----------------------------------------------------------
  /** A lip-synced examiner for the live interview trial: what the browser
   * needs to connect to the provider, scoped to one session, with the face
   * of the interviewer the sitting picked (an id from the course overview). */
  async startAvatarSession(provider, examiner = null) {
    return this.post('/api/interviews/avatar/session', examiner ? { provider, examiner } : { provider });
  }

  /** End a LiveAvatar session so it stops billing. */
  async stopAvatarSession(session) {
    return this.post('/api/interviews/avatar/stop', {
      provider: session.provider, session_id: session.session_id, session_token: session.session_token,
    });
  }

  /** The examiner's line as raw speech, streamed as it is synthesised:
   * {reader, sampleRate}, the reader yielding 16-bit little-endian mono PCM
   * (hooks/speechStream.js reads it). Not through makeRequest, which waits
   * for and parses a whole JSON body. */
  async streamSpeech(text, voice = null) {
    await this.ensureAuthFresh();
    const config = this.createRequestConfig({
      method: 'POST',
      body: JSON.stringify(voice ? { text, voice } : { text }),
      timeout: 30000,
    });
    let response;
    try {
      response = await fetch(`${this.baseURL}/api/interviews/speech/stream`, config);
    } finally {
      // The timeout guards the wait for a first answer, not the stream itself.
      clearTimeout(config._timeoutId);
    }
    if (!response.ok || !response.body) {
      throw new Error(`Speech stream failed (HTTP ${response.status})`);
    }
    return {
      reader: response.body.getReader(),
      sampleRate: Number(response.headers.get('X-Sample-Rate')) || 24000,
    };
  }

  /** voice: the interviewer's; none means the default. */
  async generateSpeech(text, voice = null) {
    return this.post('/api/interviews/speech', voice ? { text, voice } : { text });
  }

  async transcribeAudioBlob(audioBlob) {
    const arrayBuffer = await audioBlob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64Audio = btoa(binary);
    return this.post('/api/interviews/transcribe', { audio_data: base64Audio }, { timeout: MODEL_CALL_TIMEOUT_MS });
  }

  // ---- sitting feedback --------------------------------------------------
  // mode is 'written' for a written paper, 'live' for a spoken interview:
  // the report is written differently for each.
  async getSittingFeedback(runIds, { includeReport = true, mode = 'live' } = {}) {
    return this.post(
      '/api/interviews/sittings/feedback',
      { run_ids: runIds, include_narrative: includeReport, mode },
      { timeout: REPORT_TIMEOUT_MS },
    );
  }

}

export const interviewRuntimeAPI = new InterviewRuntimeAPI();
